<template>
  <BHeightAdjuster>
    <BModalBody>
      <div class="setting-message">
        {{ $t('productCategoryStatus.settingMessage') }}
      </div>
      <div
        v-loading="loading"
        class="list-content"
      >
        <div class="item-content">
          <BEmptyBox
            v-if="!loading && productCategoryStatuses.length === 0"
            class="pt-600 pb-600"
          >
            {{ $t('productCategoryStatus.empty') }}
          </BEmptyBox>
          <BLayout
            v-for="status in productCategoryStatuses"
            :key="status.id"
            class="item-row py-100"
            align-center
            justify-space-between
          >
            <BEditableContent
              class="mr-100"
              :value="status.name"
              :placeholder="$t('productCategoryStatus.name')"
              :rules="[requiredRule]"
              :loading="updatingStatusId === status.id"
              :uneditable="isApiWaiting"
              @check="handleUpdate($event, status.id)"
            />
            <BMoreMenu>
              <BBtn
                flat
                fit
                size="small"
                :loading="deletingStatusId === status.id"
                :disabled="isApiWaiting"
                @click="() => handleDelete(status)"
              >
                {{ $t('general.delete.do') }}
              </BBtn>
            </BMoreMenu>
          </BLayout>
        </div>
      </div>
    </BModalBody>
    <BModalFooter>
      <BLayout
        class="footer"
        align-center
        justify-center
      >
        <BInput
          v-model="newStatusName"
          v-model:valid="newStatusValid"
          class="input-name mr-400"
          :placeholder="$t('productCategoryStatus.name')"
          :rules="[requiredRule]"
          :disabled="isApiWaiting"
          @keypress-enter="handleCreate"
        />
        <BBtn
          type="primary"
          :disabled="!newStatusValid || isApiWaiting"
          :loading="creating"
          @click="handleCreate"
        >
          {{ $t('productCategoryStatus.create') }}
        </BBtn>
      </BLayout>
    </BModalFooter>
  </BHeightAdjuster>
</template>

<script lang="ts" setup>
import { isAxiosError } from 'axios';
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import type { ProductCategoryStatus } from '@/api/openapi';
import { ProductCategoryStatusApiService } from '@/api/user/resources/product_category';
import { useInputValidations } from '@/composable/input-validations';
import { useAlert, useConfirm } from '@/composable/message-dialog';

const { requiredRule } = useInputValidations();
const i18n = useI18n();
const { confirm } = useConfirm();
const { alert } = useAlert();
const api = new ProductCategoryStatusApiService();

const productCategoryStatuses = ref<ProductCategoryStatus[]>([]);
const loading = ref(false);
const creating = ref(false);
const updatingStatusId = ref<number | null>(null);
const deletingStatusId = ref<number | null>(null);

const newStatusName = ref('');
const newStatusValid = ref(false);

const isApiWaiting = computed(
  () =>
    updatingStatusId.value !== null
    || deletingStatusId.value !== null
    || creating.value,
);

const load = async () => {
  loading.value = true;
  try {
    const result = await api.getProductCategoryStatuses();
    productCategoryStatuses.value = result.data.productCategoryStatuses;
  } finally {
    loading.value = false;
  }
};

const handleCreate = async () => {
  if (!newStatusValid.value) return;
  creating.value = true;
  try {
    await api.postProductCategoryStatus({
      postProductCategoryStatusRequest: { productCategoryStatus: { name: newStatusName.value } },
    });
    newStatusName.value = '';
    await load();
  } finally {
    creating.value = false;
  }
};

const handleUpdate = async (name: string, statusId: number) => {
  updatingStatusId.value = statusId;
  try {
    await api.putProductCategoryStatus({
      productCategoryStatusId: statusId,
      postProductCategoryStatusRequest: { productCategoryStatus: { name } },
    });
    await load();
  } finally {
    updatingStatusId.value = null;
  }
};

const handleDelete = async (status: ProductCategoryStatus) => {
  const ok = await confirm({
    title: i18n.t('general.confirm.text'),
    message: i18n.t('productCategoryStatus.deleteConfirmMessage', { name: status.name }),
  });
  if (!ok) return;
  deletingStatusId.value = status.id;
  try {
    try {
      await api.deleteProductCategoryStatus({ productCategoryStatusId: status.id });
    } catch (e) {
      const message = isAxiosError(e) && e.response?.status === 422
        ? i18n.t('productCategory.deleteErrorRegisteredToLeadMessage', { name: status.name })
        : i18n.t('general.fail.to', { action: i18n.t('general.delete.text') });
      await alert({
        title: i18n.t('general.error'),
        message,
      });
      return;
    }
    await load();
  } finally {
    deletingStatusId.value = null;
  }
};

onMounted(async () => {
  await load();
});
</script>

<style lang="scss" scoped>
:deep(.b-modal-body) {
  margin-top: 20px;
  padding: 0 32px 20px;
  .list-content {
    overflow-y: auto;
    .item-content {
      height: calc(100vh - 360px);
      .item-row {
        margin-left: $basespace-100;
        @media only screen and (min-width: 959px) {
          width: 50%;
        }
      }
    }
  }
}

.setting-message {
  color: $textcolor-light;
  margin-bottom: $basespace-400;
}

.footer {
  padding-top: $basespace-300;
  .input-name {
    width: $setting-input-width;
  }
}
</style>
